import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import type { ExcalidrawElement } from "../../packages/excalidraw/element/types";

/** Fit once Excalidraw has applied the requested scene and measured its canvas. */
export const fitLoadedScene = (
  api: ExcalidrawImperativeAPI,
  expectedElements: readonly ExcalidrawElement[],
  checkImmediately = true,
  onComplete?: () => void,
): (() => void) => {
  const expectedIds = expectedElements
    .filter((element) => !element.isDeleted)
    .map((element) => element.id);

  let unsubscribe: (() => void) | null = null;
  let stopped = false;
  let fitScheduled = false;
  let fitFrameId: number | null = null;
  let completeFrameId: number | null = null;

  const stopListening = () => {
    unsubscribe?.();
    unsubscribe = null;
  };

  const completeAfterPaint = () => {
    if (!onComplete || stopped) {
      return;
    }
    completeFrameId = requestAnimationFrame(() => {
      completeFrameId = null;
      if (!stopped) {
        onComplete();
      }
    });
  };

  if (!expectedIds.length) {
    completeAfterPaint();
    return () => {
      stopped = true;
      if (completeFrameId !== null) {
        cancelAnimationFrame(completeFrameId);
      }
    };
  }

  const tryFit = () => {
    if (stopped || fitScheduled) {
      return;
    }
    const { isLoading, width, height } = api.getAppState();
    if (isLoading || width <= 0 || height <= 0) {
      return;
    }
    const elements = api.getSceneElements();
    const loadedIds = new Set(elements.map((element) => element.id));
    if (!expectedIds.every((id) => loadedIds.has(id))) {
      return;
    }
    fitScheduled = true;
    stopListening();
    // Excalidraw emits this change while committing its initialization state.
    // Fit on the next frame so that commit cannot overwrite the viewport update.
    fitFrameId = requestAnimationFrame(() => {
      fitFrameId = null;
      if (stopped) {
        return;
      }
      api.scrollToContent(elements, { fitToContent: true });
      completeAfterPaint();
    });
  };

  unsubscribe = api.onChange(tryFit);
  if (checkImmediately) {
    tryFit();
  }
  return () => {
    stopped = true;
    stopListening();
    if (fitFrameId !== null) {
      cancelAnimationFrame(fitFrameId);
    }
    if (completeFrameId !== null) {
      cancelAnimationFrame(completeFrameId);
    }
  };
};
